<!--
  An inline version of ElFormItem
  Errors is displayed in a slider, or in an ElFormErrors

  It has one exta prop compared to ElFormItem:
  - field: the name the field should be references as (default: prop)
-->

<template>
  <div class="el-form-item el-form-small-item" :class="[{
      'el-form-item--feedback': elForm && elForm.statusIcon,
      'is-error': validateState === 'error',
      'is-validating': validateState === 'validating',
      'is-success': validateState === 'success',
      'is-required': isRequired || required,
      'is-no-asterisk': elForm && elForm.hideRequiredAsterisk,
      'is-flex': flex,
    },
    sizeClass ? 'el-form-item--' + sizeClass : ''
  ]">
    <slot></slot>
    <slide-up-down
      v-if="!elFormErrors"
      :active="validateState === 'error' && showMessage && (!form || form.showMessage)"
      :duration="200">
      <slot
        name="error"
        :error="validateMessage">
        <div
          class="el-form-item__error"
          :class="{
            'el-form-item__error--inline': typeof inlineMessage === 'boolean'
              ? inlineMessage
              : (elForm && elForm.inlineMessage || false)
          }"
        >
          {{validateMessage}}
        </div>
      </slot>
    </slide-up-down>
  </div>
</template>
<script>
import AsyncValidator from 'async-validator'
import emitter from 'element-ui/lib/mixins/emitter'
import objectAssign from 'element-ui/lib/utils/merge'
import { noop, getPropByPath } from 'element-ui/lib/utils/util'
import SlideUpDown from 'vue-slide-up-down'

export default {
  name: 'ElFormSmallItem',

  componentName: 'ElFormItem',

  mixins: [emitter],

  provide () {
    return {
      elFormItem: this,
    }
  },

  components: {
    SlideUpDown,
  },

  inject: {
    elForm: {
      default: null,
    },
    elFormErrors: {
      default: null,
    },
  },

  props: {
    prop: String,
    required: {
      type: Boolean,
      default: undefined,
    },
    rules: [Object, Array],
    error: String,
    validateStatus: String,
    inlineMessage: {
      type: [String, Boolean],
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
    size: String,
    flex: {
      type: Boolean,
      default: true,
    },
    // the name to display in error messages
    field: String,
  },
  watch: {
    error: {
      immediate: true,
      handler (value) {
        this.validateMessage = value
        this.validateState = value ? 'error' : ''
      },
    },
    validateStatus (value) {
      this.validateState = value
    },
  },
  computed: {
    form () {
      let parent = this.$parent
      let parentName = parent.$options.componentName
      while (parentName !== 'ElForm') {
        if (parentName === 'ElFormItem') {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isNested = true
        }
        parent = parent.$parent
        if (!parent) break
        parentName = parent.$options.componentName
      }
      return parent
    },
    fieldValue () {
      const model = this.form && this.form.model
      if (!model || !this.prop) { return }

      let path = this.prop
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.')
      }

      return getPropByPath(model, path, true).v
      // try {
      //   return getPropByPath(model, path, true).v
      // } catch (e) {
      //   console.error('invalid prop', this.prop, JSON.stringify(model))
      //   throw e
      // }
    },
    isRequired () {
      let rules = this.getRules()
      let isRequired = false

      if (rules && rules.length) {
        rules.every(rule => {
          if (rule.required) {
            isRequired = true
            return false
          }
          return true
        })
      }
      return isRequired
    },
    _formSize () {
      return this.elForm && this.elForm.size
    },
    elFormItemSize () {
      return this.size || this._formSize
    },
    sizeClass () {
      return this.elFormItemSize || (this.$ELEMENT || {}).size
    },
  },
  data () {
    return {
      validateState: '',
      validateMessage: '',
      validateDisabled: false,
      validator: {},
      isNested: false,
    }
  },
  methods: {
    validate (trigger, callback = noop) {
      this.validateDisabled = false
      const rules = this.getFilteredRule(trigger)
      if ((!rules || rules.length === 0) && this.required === undefined) {
        callback()
        return true
      }

      this.validateState = 'validating'

      const field = this.field || this.prop
      const descriptor = {}
      if (rules && rules.length > 0) {
        rules.forEach(rule => {
          delete rule.trigger
        })
      }
      descriptor[field] = rules

      const validator = new AsyncValidator(descriptor)
      const model = {}

      model[field] = this.fieldValue

      validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''

        callback(this.validateMessage, invalidFields)
        this.elForm && this.elForm.$emit('validate', this.prop, !errors, this.validateMessage || null)
      })
    },
    clearValidate () {
      this.validateState = ''
      this.validateMessage = ''
      this.validateDisabled = false
    },
    resetField () {
      this.validateState = ''
      this.validateMessage = ''

      let model = this.form && this.form.model
      let value = this.fieldValue
      let path = this.prop
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.')
      }

      let prop = getPropByPath(model, path, true)

      this.validateDisabled = true
      if (Array.isArray(value)) {
        prop.o[prop.k] = [].concat(this.initialValue)
      } else {
        prop.o[prop.k] = this.initialValue
      }

      this.broadcast('ElTimeSelect', 'fieldReset', this.initialValue)
    },
    getRules () {
      let formRules = this.form && this.form.rules
      const selfRules = this.rules
      const requiredRule = this.required !== undefined ? { required: !!this.required } : []

      const prop = getPropByPath(formRules, this.prop || '')
      formRules = formRules ? (prop.o[this.prop || ''] || prop.v) : []

      return [].concat(selfRules || formRules || []).concat(requiredRule)
    },
    getFilteredRule (trigger) {
      const rules = this.getRules()

      return rules.filter(rule => {
        if (!rule.trigger || trigger === '') return true
        if (Array.isArray(rule.trigger)) {
          return rule.trigger.indexOf(trigger) > -1
        } else {
          return rule.trigger === trigger
        }
      }).map(rule => objectAssign({}, rule))
    },
    onFieldBlur () {
      this.validate('blur')
    },
    onFieldChange () {
      if (this.validateDisabled) {
        this.validateDisabled = false
        return
      }

      this.validate('change')
    },
  },
  mounted () {
    if (this.prop) {
      this.dispatch('ElForm', 'el.form.addField', [this])
      if (this.elFormErrors) this.elFormErrors.addField(this)

      let initialValue = this.fieldValue
      if (Array.isArray(initialValue)) {
        initialValue = [].concat(initialValue)
      }
      Object.defineProperty(this, 'initialValue', {
        value: initialValue,
      })

      let rules = this.getRules()

      if (rules.length || this.required !== undefined) {
        this.$on('el.form.blur', this.onFieldBlur)
        this.$on('el.form.change', this.onFieldChange)
      }
    }
  },
  beforeDestroy () {
    this.dispatch('ElForm', 'el.form.removeField', [this])
    if (this.elFormErrors) this.elFormErrors.removeField(this)
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .el-form-item.el-form-small-item {
    margin: 0;
    margin-bottom: 0 !important;
    transition: 0.3s ease-out;
  }
  .el-form-item.el-form-small-item > .el-autocomplete {
    display: block;
  }
  .el-button-group > .el-form-item.el-form-small-item {
    height: $--input-height;
  }
</style>
